import { useState } from 'react';
import { useOrdensProducao, useCriarOrdemProducao } from '@/hooks/useProducao';
import { useMaquinas, useIniciarImpressao } from '@/hooks/useMaquinas';
import { useProdutos } from '@/hooks/useProdutos';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Plus, Printer, Clock, CheckCircle } from 'lucide-react';
import { formatDate } from '@/lib/utils';
import type { OrdemProducao } from '@/types';

const statusConfig: Record<string, { label: string; variant: any }> = {
  NOVO: { label: 'Novo', variant: 'secondary' },
  PAGO_CONFIRMADO: { label: 'Confirmado', variant: 'info' },
  PRODUCAO: { label: 'Em Produção', variant: 'warning' },
  AGUARDANDO_SEPARACAO: { label: 'Pronto', variant: 'success' },
  PRONTO_EXPEDICAO: { label: 'Pronto Expedição', variant: 'success' },
  ENVIADO: { label: 'Enviado', variant: 'info' },
  ENTREGUE: { label: 'Entregue', variant: 'success' },
  CANCELADO: { label: 'Cancelado', variant: 'error' },
};

export function FilaProducao() {
  const { data: ordens, isLoading } = useOrdensProducao();
  const { data: maquinas } = useMaquinas();
  const { data: produtos } = useProdutos();
  const criarOrdem = useCriarOrdemProducao();
  const iniciarImpressao = useIniciarImpressao();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    produtoId: '',
    maquinaId: '',
    quantidadeSolicitada: '1',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await criarOrdem.mutateAsync({
      produtoId: formData.produtoId,
      maquinaId: formData.maquinaId,
      quantidadeSolicitada: Number(formData.quantidadeSolicitada),
    });
    setOpen(false);
  };

  const ordensEmProducao = ordens?.filter((o: OrdemProducao) => o.status === 'PRODUCAO');
  const ordensPendentes = ordens?.filter((o: OrdemProducao) => o.status === 'NOVO');
  const ordensConcluidas = ordens?.filter((o: OrdemProducao) => o.status === 'AGUARDANDO_SEPARACAO' as any);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fila de Produção</h1>
          <p className="text-gray-500">Gerencie as ordens de produção</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="nuvem" leftIcon={<Plus className="h-4 w-4" />}>
              Nova Ordem
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>Criar Ordem de Produção</DialogTitle>
              <DialogDescription>
                Selecione o produto e a máquina para iniciar a produção.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Produto</label>
                <Select value={formData.produtoId} onValueChange={(v) => setFormData({ ...formData, produtoId: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um produto" />
                  </SelectTrigger>
                  <SelectContent>
                    {produtos?.map((p: any) => p.id ? (
                      <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                    ) : null)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Máquina</label>
                <Select value={formData.maquinaId} onValueChange={(v) => setFormData({ ...formData, maquinaId: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione uma máquina" />
                  </SelectTrigger>
                  <SelectContent>
                    {maquinas?.map((m: any) => m.id ? (
                      <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>
                    ) : null)}
                  </SelectContent>
                </Select>
              </div>
              <Input
                label="Quantidade"
                type="number"
                min="1"
                value={formData.quantidadeSolicitada}
                onChange={(e) => setFormData({ ...formData, quantidadeSolicitada: e.target.value })}
                required
              />
              <Button type="submit" variant="nuvem" className="w-full" isLoading={criarOrdem.isPending}>
                Criar Ordem
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Resumo */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="rounded-full bg-fort3d-ciano/10 p-3">
              <Printer className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Em Produção</p>
              <p className="text-2xl font-bold text-gray-900">{ordensEmProducao?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="rounded-full bg-fort3d-amarelo/10 p-3">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Pendentes</p>
              <p className="text-2xl font-bold text-gray-900">{ordensPendentes?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 py-4">
            <div className="rounded-full bg-fort3d-verde/10 p-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Concluídas Hoje</p>
              <p className="text-2xl font-bold text-gray-900">{ordensConcluidas?.length || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Ordens */}
      <Card>
        <CardHeader>
          <CardTitle>Ordens de Produção</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Nº OP</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Produto</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Máquina</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Qtd</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Status</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Progresso</th>
                    <th className="py-3 text-left text-sm font-medium text-gray-500">Criado em</th>
                    <th className="py-3 text-right text-sm font-medium text-gray-500">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ordens?.map((ordem: OrdemProducao) => (
                    <tr key={ordem.id} className="border-b border-gray-200/50">
                      <td className="py-4 font-mono text-primary">{ordem.numeroOP}</td>
                      <td className="py-4 text-gray-900">{ordem.produto?.nome}</td>
                      <td className="py-4 text-gray-500">
                        {ordem.maquina?.nome}
                        {ordem.maquina?.ipAddress && (
                          <span className="ml-2 inline-block h-2 w-2 rounded-full bg-green-500" title="Wi-Fi Configurado" />
                        )}
                      </td>
                      <td className="py-4 text-gray-900">{ordem.quantidadeSolicitada}</td>
                      <td className="py-4">
                        <Badge variant={statusConfig[ordem.status].variant}>
                          {statusConfig[ordem.status].label}
                        </Badge>
                      </td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-24 rounded-full bg-gray-50">
                            <div
                              className="h-2 rounded-full bg-fort3d-laranja transition-all"
                              style={{ width: `${ordem.progressoPercentual}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500">{ordem.progressoPercentual}%</span>
                        </div>
                      </td>
                      <td className="py-4 text-gray-500 text-sm">
                        {formatDate(ordem.createdAt)}
                      </td>
                      <td className="py-4 text-right">
                        {ordem.status === 'NOVO' && ordem.maquina?.ipAddress && (
                          <Button 
                            variant="nuvem" 
                            size="sm"
                            leftIcon={<Printer className="h-4 w-4" />}
                            onClick={() => iniciarImpressao.mutate({ 
                              id: ordem.maquinaId, 
                              data: { 
                                produtoId: ordem.produtoId,
                                arquivoUrl: ordem.produto?.arquivo3DUrl || '',
                                nomeArquivo: ordem.produto?.nomeArquivo3D || `${ordem.numeroOP}.gcode`
                              } 
                            })}
                            isLoading={iniciarImpressao.isPending}
                          >
                            Imprimir
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
